import type { FC } from 'react';
import type { ImageInfo } from 'src/common/components/gallery';
import { message } from 'src/common/components/message';
import { useAppTranslation } from '@flowus/common/i18n';
import { IconPanel } from 'src/components/icon-trigger/icon-panel';
import { releaseLocalUrl } from 'src/common/utils/release-local-url';
import { useUpload } from 'src/hooks/space/use-upload';
import { useTransaction } from 'src/hooks/use-transaction';
import { MessageIds } from 'src/modals';
import { updateBlock } from 'src/redux/managers/block/update';
import { cache } from 'src/redux/store';

interface Props {
  blockId: string;
  onCloseModal: () => void;
}

export const IconEditPanel: FC<Props> = ({ blockId, onCloseModal }) => {
  const { t } = useAppTranslation();
  const transaction = useTransaction();
  const upload = useUpload();

  const onSelectFile = (file: File) => {
    updateBlock(
      blockId,
      {
        data: {
          icon: {
            type: 'upload',
            value: '',
          },
          iconUrl: URL.createObjectURL(file),
        },
      },
      true
    );
    message.loading({
      key: MessageIds.UPLOAD_ICON,
      content: t('common.uploading_ellipsis'),
    });
    void upload({
      file,
      type: 'file',
      searchType: 'icon',
      onComplete: (ret) => {
        message.closeMessage(MessageIds.UPLOAD_ICON);
        if (ret.success) {
          message.success(t('components.icon_trigger.upload_success'));
          transaction(() => {
            updateBlock(blockId, {
              data: { icon: { type: 'upload', value: ret.ossName ?? '' } },
            });
          });
        }
      },
    });
  };

  const onSelectRecentlyImage = (imageInfo: ImageInfo) => {
    transaction(() => {
      updateBlock(blockId, {
        data: {
          icon: { type: 'upload', value: imageInfo.ossName },
          iconUrl: imageInfo.url,
        },
      });
    });
  };

  const block = cache.blocks[blockId];
  const linkUrl = block?.data.icon?.type === 'http' ? block.data.icon.value : '';

  return (
    <IconPanel
      hideRemoveBtn={false}
      inputValue={linkUrl}
      onSelectEmoji={(emojiStr, isFromRandom) => {
        if (!isFromRandom) onCloseModal();
        transaction(() => {
          updateBlock(blockId, {
            data: {
              icon: { type: 'emoji', value: emojiStr },
              iconUrl: '',
            },
          });
        });
      }}
      onSelectCustomIcon={(iconPath, isFromRandom) => {
        if (!isFromRandom) onCloseModal();
        transaction(() => {
          updateBlock(blockId, {
            data: {
              icon: { type: 'icon', value: iconPath },
              iconUrl: '',
            },
          });
        });
      }}
      onUploadFile={(file) => {
        if (file.size >= 20 * 1024 * 1024) {
          message.error(t('components.icon_trigger.icon_size_limit'));
          return;
        }
        onCloseModal();
        onSelectFile(file);
      }}
      onSelectRecentlyImage={(imageInfo) => {
        onCloseModal();
        onSelectRecentlyImage(imageInfo);
      }}
      onInputUrl={(url) => {
        transaction(() => {
          updateBlock(blockId, {
            data: {
              icon: { type: 'http', value: url },
              iconUrl: url,
            },
          });
        });
        onCloseModal();
      }}
      onRemoveBtnClick={() => {
        const block = cache.blocks[blockId];
        if (block) {
          releaseLocalUrl(block);
        }
        transaction(() => {
          updateBlock(blockId, {
            data: {
              icon: {},
              iconUrl: undefined,
            },
          });
        });
        onCloseModal();
      }}
    />
  );
};
